/**
 * Subscribe & Save Collection Mode
 *
 * When ?selling_plan=subscribe is in the URL:
 * 1. Fetches subscription pricing for all products
 * 2. Swaps displayed prices to show subscription discounts
 * 3. Adds "Subscribe & Save" badges to product cards
 * 4. Intercepts quick-add so items go to cart as subscriptions
 */
(function () {
  'use strict';

  // ── Activation check ──────────────────────────────────────
  var params = new URLSearchParams(window.location.search);
  var isSubscribeMode =
    params.get('selling_plan') === 'subscribe' ||
    window.location.hash === '#subscribe-and-save';

  // ── Auto-tag links on the Subscribe & Save page ───────────
  if (window.location.pathname.includes('subscribe-and-save')) {
    document.querySelectorAll('a[href*="/collections/"]').forEach(function (link) {
      if (!link.href.includes('selling_plan=')) {
        var separator = link.href.includes('?') ? '&' : '?';
        link.href = link.href + separator + 'selling_plan=subscribe';
      }
    });
  }

  if (!isSubscribeMode) return;

  // ── Config ────────────────────────────────────────────────
  var BADGE_TEXT = 'Subscribe & Save';
  var FETCH_DELAY_MS = 100;

  // ── State ─────────────────────────────────────────────────
  var sellingPlanMap = {};
  var handleToPlan = {};

  // ── 1. Inject styles ──────────────────────────────────────
  var style = document.createElement('style');
  style.textContent = [
    '.subscribe-save-badge {',
    '  display: inline-block;',
    '  background: #2d6a31;',
    '  color: #fff;',
    '  font-size: 11px;',
    '  font-weight: 700;',
    '  padding: 3px 8px;',
    '  border-radius: 3px;',
    '  letter-spacing: 0.03em;',
    '  text-transform: uppercase;',
    '  margin-bottom: 4px;',
    '}',
    '.subscribe-price-wrapper {',
    '  display: flex;',
    '  flex-direction: column;',
    '  align-items: center;',
    '  gap: 2px;',
    '}',
    '.subscribe-price-original {',
    '  text-decoration: line-through;',
    '  opacity: 0.55;',
    '  font-size: 0.85em;',
    '}',
    '.subscribe-price-new {',
    '  color: #2d6a31;',
    '  font-weight: 700;',
    '}'
  ].join('\n');
  document.head.appendChild(style);

  // ── 2. Find all product cards ─────────────────────────────
  function getProductCards() {
    var cards = [];
    var links = document.querySelectorAll('a[href*="/products/"]');
    var seen = new Set();

    links.forEach(function (link) {
      var match = link.href.match(/\/products\/([^?#/]+)/);
      if (!match) return;

      var handle = match[1];
      var variantMatch = link.href.match(/variant=(\d+)/);
      var variantId = variantMatch ? variantMatch[1] : null;

      var card = link;
      for (var i = 0; i < 6; i++) {
        if (!card.parentElement) break;
        card = card.parentElement;
        if (
          card.tagName === 'LI' ||
          card.tagName === 'ARTICLE' ||
          (card.className &&
            (card.className.includes('card') ||
              card.className.includes('product') ||
              card.className.includes('grid__item')))
        ) {
          break;
        }
      }

      if (seen.has(card)) return;
      seen.add(card);

      cards.push({
        element: card,
        handle: handle,
        variantId: variantId,
        link: link
      });
    });

    return cards;
  }

  // ── 3. Fetch selling plan data ────────────────────────────
  async function fetchProductData(handle) {
    try {
      var res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.warn('[Subscribe Mode] Failed to fetch:', handle, e);
      return null;
    }
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // ── 4. Extract selling plan info ──────────────────────────
  function getSellingPlanInfo(product) {
    if (!product.selling_plan_groups || product.selling_plan_groups.length === 0) {
      return null;
    }

    var group = product.selling_plan_groups[0];
    if (!group.selling_plans || group.selling_plans.length === 0) return null;

    var plan = group.selling_plans[0];
    var adjustment = plan.price_adjustments && plan.price_adjustments[0];
    var discountType = adjustment ? adjustment.value_type : null;
    var discountValue = adjustment ? adjustment.value : 0;

    var variantPrices = {};
    product.variants.forEach(function (variant) {
      var originalPrice = variant.price;
      var discountedPrice = originalPrice;

      if (discountType === 'percentage') {
        discountedPrice = Math.round(originalPrice * (1 - discountValue / 100));
      } else if (discountType === 'fixed_amount') {
        discountedPrice = originalPrice - discountValue;
      } else if (discountType === 'price') {
        discountedPrice = discountValue;
      }

      variantPrices[variant.id] = {
        originalPrice: originalPrice,
        discountedPrice: discountedPrice,
        planId: plan.id,
        planName: plan.name,
        discountPercent: discountType === 'percentage' ? discountValue : null
      };

      sellingPlanMap[variant.id] = variantPrices[variant.id];
    });

    handleToPlan[product.handle] = plan.id;

    return {
      planId: plan.id,
      planName: plan.name,
      variantPrices: variantPrices
    };
  }

  // ── 5. Update a product card ──────────────────────────────
  function updateCard(card, planInfo, product) {
    if (!planInfo) return;

    var el = card.element;

    if (el.dataset.subscribeUpdated) return;
    el.dataset.subscribeUpdated = 'true';

    var variantId = card.variantId || String(product.variants[0].id);
    var priceInfo = planInfo.variantPrices[variantId];
    if (!priceInfo) return;

    var originalFormatted = formatMoney(priceInfo.originalPrice);
    var discountedFormatted = formatMoney(priceInfo.discountedPrice);

    var priceContainer = findOutermostPriceContainer(el, originalFormatted);

    if (priceContainer) {
      priceContainer.innerHTML =
        '<span class="subscribe-price-wrapper">' +
        '<span class="subscribe-price-original">' + originalFormatted + '</span>' +
        '<span class="subscribe-price-new">' + discountedFormatted + '</span>' +
        '</span>';
    }

    var titleEl = el.querySelector('h3, h4, .product-card__title, .card__title, .product-title');
    if (titleEl && !el.querySelector('.subscribe-save-badge')) {
      var badge = document.createElement('span');
      badge.className = 'subscribe-save-badge';
      badge.textContent = BADGE_TEXT;
      titleEl.parentNode.insertBefore(badge, titleEl);
    }

    var forms = el.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(function (form) {
      if (!form.querySelector('input[name="selling_plan"]')) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.value = priceInfo.planId;
        form.appendChild(input);
      }
    });

    el.dataset.sellingPlan = priceInfo.planId;
    el.dataset.variantId = variantId;
  }

  // ── 6. Find outermost price container ─────────────────────
  function findOutermostPriceContainer(container, priceText) {
    var cleanPrice = priceText.replace(/\s/g, '');

    var priceEls = container.querySelectorAll('.price');
    for (var i = 0; i < priceEls.length; i++) {
      var text = priceEls[i].textContent.replace(/\s/g, '');
      if (text.includes(cleanPrice)) {
        return priceEls[i];
      }
    }

    var candidates = container.querySelectorAll('span, p, div');
    var innermost = null;
    var innermostSize = Infinity;

    for (var j = 0; j < candidates.length; j++) {
      var elem = candidates[j];
      if (elem.tagName === 'SCRIPT' || elem.tagName === 'STYLE') continue;
      var elemText = elem.textContent.replace(/\s/g, '');
      if (elemText.includes(cleanPrice) && elem.children.length === 0) {
        var size = elemText.length;
        if (size < innermostSize) {
          innermostSize = size;
          innermost = elem;
        }
      }
    }

    if (!innermost) return null;

    var best = innermost;
    var current = innermost.parentElement;
    var maxWalk = 3;

    while (current && maxWalk > 0 && current !== container) {
      var parentText = current.textContent.trim();
      var isPriceOnly = parentText.length < 40 &&
        parentText.includes('$') &&
        !parentText.match(/[a-zA-Z]{6,}/);

      if (isPriceOnly) {
        best = current;
      } else {
        break;
      }
      current = current.parentElement;
      maxWalk--;
    }

    return best;
  }

  // ── 7. Format cents to dollar string ──────────────────────
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ── 8. Intercept fetch for cart/add ───────────────────────
  var originalFetch = window.fetch;
  window.fetch = function (url, options) {
    if (typeof url === 'string' && url.includes('/cart/add')) {
      try {
        if (options && options.body) {
          if (options.body instanceof FormData) {
            var variantId = options.body.get('id');
            if (variantId && sellingPlanMap[variantId] && !options.body.get('selling_plan')) {
              options.body.set('selling_plan', sellingPlanMap[variantId].planId);
              console.log('[Subscribe Mode] Injected selling_plan', sellingPlanMap[variantId].planId, 'for variant', variantId);
            }
          } else if (typeof options.body === 'string') {
            try {
              var body = JSON.parse(options.body);
              var vid = String(body.id || (body.items && body.items[0] && body.items[0].id));
              if (vid && sellingPlanMap[vid] && !body.selling_plan) {
                body.selling_plan = sellingPlanMap[vid].planId;
                if (body.items && body.items[0]) {
                  body.items[0].selling_plan = sellingPlanMap[vid].planId;
                }
                options.body = JSON.stringify(body);
                console.log('[Subscribe Mode] Injected selling_plan (JSON)', sellingPlanMap[vid].planId);
              }
            } catch (e) { /* Not JSON */ }
          }
        }
      } catch (e) {
        console.warn('[Subscribe Mode] Error intercepting cart/add:', e);
      }
    }
    return originalFetch.apply(this, arguments);
  };

  var originalXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._url = url;
    return originalXHROpen.apply(this, arguments);
  };
  var originalXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (body) {
    if (this._url && this._url.includes('/cart/add') && body) {
      try {
        if (typeof body === 'string') {
          var parsed = JSON.parse(body);
          var vid = String(parsed.id);
          if (vid && sellingPlanMap[vid] && !parsed.selling_plan) {
            parsed.selling_plan = sellingPlanMap[vid].planId;
            body = JSON.stringify(parsed);
            console.log('[Subscribe Mode] Injected selling_plan (XHR)', sellingPlanMap[vid].planId);
          }
        }
      } catch (e) { /* Not JSON */ }
    }
    return originalXHRSend.call(this, body);
  };

  // ── 9. Main initialization ────────────────────────────────
  async function init() {
    var cards = getProductCards();
    console.log('[Subscribe Mode] Found', cards.length, 'product cards');

    var uniqueHandles = [];
    var handleSet = {};
    cards.forEach(function (card) {
      if (!handleSet[card.handle]) {
        handleSet[card.handle] = true;
        uniqueHandles.push(card.handle);
      }
    });

    for (var i = 0; i < uniqueHandles.length; i++) {
      var handle = uniqueHandles[i];
      var product = await fetchProductData(handle);
      if (product) {
        var planInfo = getSellingPlanInfo(product);
        if (planInfo) {
          cards.forEach(function (card) {
            if (card.handle === handle) {
              updateCard(card, planInfo, product);
            }
          });
        }
      }
      if (i < uniqueHandles.length - 1) {
        await sleep(FETCH_DELAY_MS);
      }
    }

    console.log(
      '[Subscribe Mode] Done. Products with plans:',
      Object.keys(handleToPlan).length, '/', uniqueHandles.length
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();